import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import socket from '../socket';
import { renderClarifyingQuestions, renderSources } from '../utils/updateConversation';
import { AppDispatch, RootState } from '../redux/store';
import {
  ADD_EVENT,
  ADD_STREAM_EVENT,
  LOAD_EVENT,
  UPDATE_EVENT_BY_DIV_ID,
} from '../redux/reducers/eventReducer';
import getTimeStamp from '../utils/getTimeStamp';
import indexedDBService from '../utils/idb.utils';
import safelyParseJSON from '../utils/safelyParseJson';
import { START_STREAM, STOP_STREAM } from '../redux/reducers/loaderReducer';
import { capitalizeName } from '../utils/pipes';
import { UserChatResponse } from '../types/chat.types';
import { playSound } from '../utils/playNotification';

function useChatWidget() {
  const roomId = localStorage.getItem('userNumber');
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const chatHistoryDivSet = useRef(new Set());
  const dataRef = useRef({});
  const dispatch = useDispatch<AppDispatch>();
  const events = useSelector((state: RootState) => state.events);
  const isMute = useSelector((state: RootState) => state.audio?.mute ?? false)
  const customerUsername = localStorage.getItem("userName") ||  "";

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  };

  const sendChat = (msg: string) => {
    socket.emit('sendchat', msg, roomId);
    scrollToBottom();
  };

  const sendFile = (fileName: string, fileType: string, divId: string) => {
    socket.emit('send_file', fileName, fileType, divId, roomId);
    scrollToBottom();
  };

  function onUpdateChatStream(
    username: string,
    data: any,
    divId = null,
    sources: any = null,
    userId: string,
    clarifyingQuestions: any = null,
  ) {
    let sourceObj: any;
    const cleanedData = data.replace(/\u2014/g, ' ');
    const dataParsed =
      cleanedData.trim() === '░'
        ? `${customerUsername ? ` ${capitalizeName(customerUsername)}` : ''}`
        : cleanedData;
    if (
      username === 'Chat Bot' &&
      divId !== null &&
      !chatHistoryDivSet.current.has(divId)
    ) {
      dispatch({ type: 'REMOVE_LOADER_EVENT' });
    }
    if (dataParsed === '[end]' && divId) {
      const payload = dataRef.current[divId] as {
        msg: string;
        [key: string]: any;
      };

      if (!isMute) {
        playSound();
      }
      dispatch({ type: STOP_STREAM });
      dispatch({ type: ADD_STREAM_EVENT, payload });

      const promptForOptions = `Provide 3 very brief, short and clear reply options phrased from user's perspective for the following user-facing assistant message: ${payload.msg}. Return a JSON object with key "options" containing an array of strings. Output only JSON.`;
      socket.emit(
        'options_suggestion',
        promptForOptions,
        payload.msg,
        roomId,
        divId,
      );

        if (sources !== null && sources !== "") {
          if (divId && sources) {
            sourceObj = safelyParseJSON(sources); // JSON.parse(sources);
            sourceObj = sourceObj && sourceObj.sources;
            renderSources(sourceObj, divId);
          }
        }
        return;
      }

      if (username === 'Chat Bot' && divId !== null) {
        const timeStamp = getTimeStamp();
        if (chatHistoryDivSet.current.has(divId)) {
          const testData1 = document.querySelector(`#${divId}`);
          if (testData1) {
            testData1.textContent = testData1.textContent + dataParsed;
            const payload = {
              type: 'chat',
              id: divId,
              user: username,
              senderType: username,
              msg: testData1.textContent,
              time: timeStamp,
              divId,
              sourceObj,
              clarifyingQuestions
            };
            dataRef.current = {
              ...dataRef.current,
              [payload.id]: { ...payload },
            };
            dispatch({
              type: UPDATE_EVENT_BY_DIV_ID,
              payload,
            });
          }
          // renderSources(sourceObj, divId);
        } else {
          dispatch({ type: START_STREAM });
          chatHistoryDivSet.current.add(divId);
          const payload = {
            type: 'chat',
            id: divId,
            user: username,
            time: timeStamp,
            timestamp: new Date().getTime(),
            senderType: username,
            msg: dataParsed,
            divId,
            sourceObj,
            clarifyingQuestions
          };
          dataRef.current = {
            ...dataRef.current,
            [payload.id]: { ...payload },
          };
          dispatch({
            type: ADD_EVENT,
            payload,
            streamEvent: true,
          });
          scrollToBottom();
        }
      }
      scrollToBottom();
    }

    async function onFileReceive(_username: string, file: any) {
      if (file) {
        const divId = file.divId;
        if (events.find((e: any) => e.id === divId)) {
          return;
        }
        const event = await indexedDBService.getEventById(divId);
        dispatch({
          type: LOAD_EVENT,
          payload: {
            event,
          },
        });
      }
    }

  function onSuggestions(
    username: string,
    dataArray: UserChatResponse | string | any,
    divId: string,
  ) {
    const questionsArray = dataArray;
    const timeStamp = getTimeStamp();
    const payload = {
      type: 'chat',
      id: divId,
      user: 'Chat Bot',
      senderType: 'Chat Bot',
      msg: 'kjvghhjh',
      time: timeStamp,
      divId,
    };

    dispatch({
      type: UPDATE_EVENT_BY_DIV_ID,
      payload,
    });
    renderClarifyingQuestions(questionsArray, divId, username);
  }

  useEffect(() => {
    socket.on('updatechat_stream', onUpdateChatStream);
    socket.on('file_receive', onFileReceive);
    socket.on('suggestions', onSuggestions);

    return () => {
      socket.off('updatechat_stream', onUpdateChatStream);
      socket.off('file_receive', onFileReceive);
      socket.off('suggestions', onSuggestions);
    };
  }, [ events]);

  return {
    messagesEndRef,
    scrollToBottom,
    sendChat,
    sendFile,
  };
}

export default useChatWidget;